import { styled } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import type { HeaderGroup } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { Checkbox } from '../../../Inputs';
import { useDataProvider } from '../../DataProviderContext';
import { useIsFilterVisible } from '../../filter/FilterContext';
import { TableHeadFilter } from '../../filter/TableHeadFilter';
import { HeadCell } from './HeadCell';
import { Sort } from './Sort';

const PlaceholderHeadCell = styled(TableCell, {
  name: 'HeadRow',
  slot: 'PlaceholderHeadCell',
})(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: 0,
}));

interface HeadRowProps {
  headerGroup: HeaderGroup<any>;
}

export const HeadRow = ({ headerGroup }: HeadRowProps) => {
  const { table, isRowIndeterminate } = useDataProvider();
  const [isFilterVisible] = useIsFilterVisible();
  const enableRowSelection = Boolean(table?.options.enableRowSelection);

  if (!table) {
    return null;
  }

  const rows = table.getRowModel().rows;
  const isAllRowsSelected = table.getIsAllRowsSelected();
  const isSomeRowsSelected =
    !isAllRowsSelected &&
    rows.some(
      (row) => row.getIsSelected() || (isRowIndeterminate?.(row) ?? false)
    );

  return (
    <TableRow key={headerGroup.id}>
      {enableRowSelection && (
        <HeadCell padding="checkbox" sx={{ verticalAlign: 'middle' }}>
          <Checkbox
            indeterminate={isSomeRowsSelected}
            checked={isAllRowsSelected}
            onClick={() => {
              table?.toggleAllRowsSelected();
            }}
          />
        </HeadCell>
      )}
      {headerGroup.headers.map((header) => {
        if (header.isPlaceholder) {
          return <PlaceholderHeadCell key={header.id} />;
        }
        return (
          <HeadCell
            sortable={header.column.getCanSort()}
            colSpan={header.colSpan}
            columnGroup={header.subHeaders.length > 0}
            key={header.id}
          >
            {isFilterVisible && header.column.getCanFilter() && (
              <TableHeadFilter column={header.column} />
            )}
            <div onClick={header.column.getToggleSortingHandler()}>
              {flexRender(header.column.columnDef.header, header.getContext())}
              <Sort column={header.column} />
            </div>
          </HeadCell>
        );
      })}
    </TableRow>
  );
};
